<script setup lang="ts">
import { computed, ref } from 'vue'
import { PhList, PhSignOut, PhX } from '@phosphor-icons/vue'
import useNavMain from '@/composables/nav/main'
import useRouteHelper from '@/composables/routeHelper'
import useTithelySSO from '@/composables/sso'
import type { NavMainItem } from '@/types/nav/main'
import TrinityMobileDropdown from '@/layouts/components/TrinityMobileDropdown.vue'
import TrinityInboxLite from '@/layouts/components/TrinityInboxLite.vue'
import { useMe } from '@/queries/me'

const { data: me } = useMe()
const { redirect } = useTithelySSO()
const { nav: menu, mainNavItems, secondaryNavItems, hasGearMenuNav } = useNavMain()
const { getCurrentRoute } = useRouteHelper(menu.value)
const isPaid = computed(() => {
    return me.value?.organization.paid ?? false
})

const thubMenuEnabled = computed(() => mainNavItems.value.length > 0 || secondaryNavItems.value.length > 0)

const items = computed<NavMainItem[]>(() =>
    thubMenuEnabled.value ? [...mainNavItems.value, ...secondaryNavItems.value] : menu.value
)

const open = ref(false)

const action = (event: Event, item: NavMainItem) => {
    if (item.action) {
        event.preventDefault()

        switch (item.action) {
            case `tithely-giving`:
                redirect(`giving`)
                break
        }
    }
}
</script>

<template>
    <header class="z-[1000] flex items-center justify-between gap-4 !p-4">
        <div class="!w-9 shrink-0 grow-0">
            <button
                type="button"
                class="flex size-9 items-center justify-center"
                @click="open = !open">
                <component
                    :is="open ? PhX : PhList"
                    weight="bold"
                    class="size-7" />
            </button>
        </div>

        <h1 class="truncate text-base font-semibold">{{ getCurrentRoute?.label || `` }}</h1>

        <div class="flex shrink-0 justify-end">
            <TrinityInboxLite />
        </div>
    </header>

    <nav
        v-if="open"
        class="top-18 absolute inset-x-0 z-[1001] h-[calc(100vh-68px)] bg-primary-50 ring-1 ring-primary-100">
        <ul class="!m-0 h-full overflow-y-auto !pb-8">
            <li class="border-b border-b-primary-100">
                <TrinityMobileDropdown nav="user" />
            </li>
            <li
                v-if="isPaid && hasGearMenuNav"
                class="border-b border-b-primary-100">
                <TrinityMobileDropdown nav="church" />
            </li>
            <li
                v-for="item in items"
                :key="item.label"
                class="border-b border-b-primary-100">
                <div class="flex items-center justify-between !pr-4">
                    <a
                        :href="item.route || `#`"
                        class="flex grow items-center justify-start !px-8 !py-3 text-lg/6 font-semibold"
                        @click="action($event, item)">
                        {{ item.label }}
                    </a>
                </div>
            </li>
            <li>
                <a
                    :href="`/login/logout?tithely_ui_redirect=${encodeURIComponent('/logout')}`"
                    class="flex items-center gap-2 !px-8 !py-2 text-base/normal font-medium text-primary">
                    <div class="flex w-8 shrink-0 grow-0 items-center justify-center">
                        <PhSignOut
                            weight="duotone"
                            class="size-6" />
                    </div>
                    {{ $t(`nav.logout`) }}
                </a>
            </li>
        </ul>
    </nav>
</template>
